/**
 * @file A group automation's custom recurrence: the `schedule` object core-service stores beside
 * `days_of_week`. Days are 0 = Monday … 6 = Sunday, the BlytzCollect convention. `isSendDay` mirrors
 * messaging-service's daily check so the "Next sends" preview shows the days it will really send.
 */
import { isEqual } from 'lodash-es'
import moment, { Moment } from 'moment'

export type Nth = 1 | 2 | 3 | 4 | -1

export interface Schedule {
  repeat: 'week' | 'month'
  every: number
  days_of_week: number[]
  month_mode: 'date' | 'weekday'
  /** 1–31, or -1 for the last day of the month. */
  month_dates: number[]
  month_weekday: { nth: Nth; day: number }
  starts_on: string
  ends: {
    type: 'never' | 'on' | 'after'
    on: string | null
    after: number | null
  }
}

export const DATE_FORMAT = 'YYYY-MM-DD'
export const LAST_DAY = -1

export const DAY_NAMES = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday'
]
export const DAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
export const NTH_OPTIONS: Array<{ value: Nth; text: string }> = [
  { value: 1, text: 'first' },
  { value: 2, text: 'second' },
  { value: 3, text: 'third' },
  { value: 4, text: 'fourth' },
  { value: -1, text: 'last' }
]

/** A custom schedule seeded from the simple picker, so switching to Custom keeps the chosen days. */
export function defaultSchedule(
  daysOfWeek: number[],
  today: Moment = moment()
): Schedule {
  return {
    repeat: 'week',
    every: 1,
    days_of_week: daysOfWeek.length
      ? [...daysOfWeek].sort()
      : [today.isoWeekday() - 1],
    month_mode: 'date',
    month_dates: [today.date()],
    month_weekday: { nth: nthOf(today), day: today.isoWeekday() - 1 },
    starts_on: today.format(DATE_FORMAT),
    ends: { type: 'never', on: null, after: null }
  }
}

/** Which "first … fourth / last" weekday of its month `date` is; the 5th counts as last. */
function nthOf(date: Moment): Nth {
  const nth = Math.ceil(date.date() / 7)

  return nth > 4 ? -1 : (nth as Nth)
}

/** The body core-service stores: only the fields that apply to the chosen repeat. */
export function scheduleBody(schedule: Schedule): Schedule {
  const week = schedule.repeat === 'week'
  const byDate = !week && schedule.month_mode === 'date'

  return {
    ...schedule,
    days_of_week: week ? [...schedule.days_of_week].sort() : [],
    month_dates: byDate ? [...schedule.month_dates].sort((a, b) => a - b) : [],
    ends: {
      type: schedule.ends.type,
      on: schedule.ends.type === 'on' ? schedule.ends.on : null,
      after: schedule.ends.type === 'after' ? schedule.ends.after : null
    }
  }
}

/** Why core-service would refuse the schedule, or null. */
export function scheduleProblem(schedule: Schedule): string | null {
  if (!Number.isInteger(schedule.every) || schedule.every < 1) {
    return 'Repeat every must be 1 or more.'
  }
  if (!moment(schedule.starts_on, DATE_FORMAT, true).isValid()) {
    return 'Choose a start date.'
  }
  if (schedule.repeat === 'week' && !schedule.days_of_week.length) {
    return 'Pick at least one day.'
  }
  if (
    schedule.repeat === 'month' &&
    schedule.month_mode === 'date' &&
    !schedule.month_dates.length
  ) {
    return 'Pick at least one day of the month.'
  }
  const { ends } = schedule
  if (ends.type === 'on') {
    if (!ends.on || !moment(ends.on, DATE_FORMAT, true).isValid()) {
      return 'Choose an end date.'
    }
    if (ends.on < schedule.starts_on) return 'The end date is before the start.'
  }
  if (
    ends.type === 'after' &&
    (!Number.isInteger(ends.after) || (ends.after ?? 0) < 1)
  ) {
    return 'Ends after must be 1 or more sends.'
  }

  return null
}

function isLastDayOfMonth(date: Moment): boolean {
  return date.date() === date.daysInMonth()
}

/** Whether the schedule sends on merchant-local `date`, ignoring the end rule's send count. */
export function isSendDay(schedule: Schedule, date: Moment): boolean {
  const day = date.clone().startOf('day')
  const start = moment(schedule.starts_on, DATE_FORMAT).startOf('day')
  if (day.isBefore(start)) return false
  if (schedule.ends.type === 'on' && schedule.ends.on) {
    if (day.isAfter(moment(schedule.ends.on, DATE_FORMAT))) return false
  }
  const weekday = day.isoWeekday() - 1

  if (schedule.repeat === 'week') {
    const weeks = day
      .clone()
      .startOf('isoWeek')
      .diff(start.clone().startOf('isoWeek'), 'weeks')

    return (
      weeks % schedule.every === 0 && schedule.days_of_week.includes(weekday)
    )
  }

  const months =
    (day.year() - start.year()) * 12 + (day.month() - start.month())
  if (months % schedule.every !== 0) return false
  if (schedule.month_mode === 'date') {
    return (
      schedule.month_dates.includes(day.date()) ||
      (schedule.month_dates.includes(LAST_DAY) && isLastDayOfMonth(day))
    )
  }
  const { nth, day: target } = schedule.month_weekday
  if (weekday !== target) return false

  return nth === -1
    ? day.date() + 7 > day.daysInMonth()
    : Math.ceil(day.date() / 7) === nth
}

/**
 * The next `count` send days from `from` on, honouring the end rule. `sendsCount` is how many sends
 * already happened, for an "after N sends" end.
 */
export function nextSendDates(
  schedule: Schedule,
  from: Moment = moment(),
  count = 3,
  sendsCount = 0
): Moment[] {
  const remaining =
    schedule.ends.type === 'after'
      ? Math.max((schedule.ends.after ?? 0) - sendsCount, 0)
      : count
  const dates: Moment[] = []
  // Nothing sends before the start, so the scan begins there when it is later than `from`.
  const start = moment(schedule.starts_on, DATE_FORMAT).startOf('day')
  const day = moment.max(from.clone().startOf('day'), start)
  // Two years covers every pattern the editor can build, "every 12 months" included.
  for (let i = 0; i < 731 && dates.length < Math.min(count, remaining); i++) {
    if (isSendDay(schedule, day)) dates.push(day.clone())
    day.add(1, 'day')
  }

  return dates
}

/**
 * Whether messaging-service switched the automation off because its schedule ran out: the last
 * "after" send happened, or the end date passed. A paused automation with sends left is not ended.
 */
export function hasEnded(
  automation: {
    is_active: boolean
    schedule?: Schedule | null
    sends_count?: number
  } | null,
  today: Moment = moment()
): boolean {
  const schedule = automation?.schedule
  if (!schedule || automation?.is_active) return false
  const { ends } = schedule
  if (ends.type === 'after')
    return (automation?.sends_count ?? 0) >= (ends.after ?? 0)
  if (ends.type === 'on' && ends.on) {
    // The end date is still a send day; core treats the schedule as ended only after it.
    return today.format(DATE_FORMAT) > ends.on
  }

  return false
}

/** "Ended Jan 7, 2027" / "Ended after 13 sends", for a schedule `hasEnded` says is over. */
export function endedText(schedule: Schedule): string {
  if (schedule.ends.type === 'on' && schedule.ends.on) {
    return `Ended ${moment(schedule.ends.on, DATE_FORMAT).format('MMM D, YYYY')}`
  }
  const after = schedule.ends.after ?? 0

  return `Ended after ${after} send${after === 1 ? '' : 's'}`
}

/**
 * A deep copy with every field filled, so editing never mutates the caller's row and switching repeat
 * or month mode always has values to show. core-service omits the fields a schedule does not use.
 */
export function normalizeSchedule(schedule?: Schedule | null): Schedule | null {
  if (!schedule) return null
  const base = defaultSchedule(schedule.days_of_week ?? [])

  return {
    ...base,
    ...schedule,
    days_of_week: [...(schedule.days_of_week ?? base.days_of_week)],
    month_dates: [
      ...(schedule.month_dates?.length
        ? schedule.month_dates
        : base.month_dates)
    ],
    month_mode: schedule.month_mode ?? base.month_mode,
    month_weekday: { ...(schedule.month_weekday ?? base.month_weekday) },
    ends: { ...base.ends, ...schedule.ends }
  }
}

/** Whether `current` differs from the saved schedule; core-service resets the send count when it does. */
export function isScheduleChanged(
  saved: Schedule | null | undefined,
  current: Schedule | null
): boolean {
  const normalized = normalizeSchedule(saved)
  if (!normalized || !current) return normalized !== current

  return !isEqual(scheduleBody(normalized), scheduleBody(current))
}

/** `HH:MM` → `h:MM AM/PM`. */
export function timeLabel(value: string): string {
  return moment(value, 'HH:mm').format('h:mm A')
}

function ordinal(n: number): string {
  const suffix =
    n % 100 >= 11 && n % 100 <= 13
      ? 'th'
      : ['th', 'st', 'nd', 'rd'][n % 10] || 'th'

  return `${n}${suffix}`
}

function listText(items: string[]): string {
  if (items.length <= 1) return items.join('')

  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}

/** "every 2 weeks on Tuesday" / "monthly on the 1st and last day" — the repeat part alone. */
export function repeatText(schedule: Schedule, short = false): string {
  const unit = schedule.repeat
  const every =
    schedule.every === 1
      ? unit === 'week'
        ? 'Weekly'
        : 'Monthly'
      : `Every ${schedule.every} ${unit}s`

  if (unit === 'week') {
    const names = short ? DAY_SHORT : DAY_NAMES
    const days = [...schedule.days_of_week].sort().map(d => names[d])

    return `${every} on ${listText(days)}`
  }
  if (schedule.month_mode === 'date') {
    const dates = [...schedule.month_dates]
      .sort((a, b) => (a === LAST_DAY ? 1 : b === LAST_DAY ? -1 : a - b))
      .map(d => (d === LAST_DAY ? 'last day' : ordinal(d)))

    return `${every} on the ${listText(dates)}`
  }
  const nth = NTH_OPTIONS.find(
    o => o.value === schedule.month_weekday.nth
  )?.text

  return `${every} on the ${nth} ${DAY_NAMES[schedule.month_weekday.day]}`
}

/** The whole schedule in a sentence, for the editor's summary line. */
export function describeSchedule(schedule: Schedule, sendTime: string): string {
  const start = moment(schedule.starts_on, DATE_FORMAT)
  let text = `${repeatText(schedule)} at ${sendTime}, starting ${start.format('MMM D, YYYY')}`
  if (schedule.ends.type === 'on' && schedule.ends.on) {
    text += `, until ${moment(schedule.ends.on, DATE_FORMAT).format('MMM D, YYYY')}`
  } else if (schedule.ends.type === 'after' && schedule.ends.after) {
    text += `, ending after ${schedule.ends.after} send${schedule.ends.after === 1 ? '' : 's'}`
  }

  return `${text}.`
}
